import React, { FC } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { getImage } from '../../../../app/utlis/imageConstants';
import DynamicSVG from '../../../atoms/DynamicSvg/DynamicSvg';

interface ReliabilityEnterpriseStepsProps {
    enterpriseData: any
}

const ReliabilityEnterpriseSteps: FC<ReliabilityEnterpriseStepsProps> = ({ enterpriseData }) => {
    // Create scroll-based animations
    const { scrollYProgress } = useScroll({
        offset: ["start end", "end start"]
    });

    // Transform values based on scroll position
    const opacity = useTransform(scrollYProgress, [0, 0.2, 0.5], [0, 1, 1]);
    const y = useTransform(scrollYProgress, [0, 0.2, 0.5], [100, 0, 0]);

    // Card animation variants with better scroll response
    const itemVariants = {
        hidden: { y: 10, opacity: 0 },
        visible: (i: number) => ({
            y: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 80,
                damping: 12,
                delay: i * 0.1, // Staggered delay based on index
            }
        })
    }

    return (
        <motion.div
            className='pt-12'
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
        >
            <div style={{
                borderTop: "1.5px solid transparent",
                borderImageSource:
                    `linear-gradient(90deg, rgb(49, 49, 49) 19%, var(--color-primary) 26%, rgb(49, 49, 49) 46%, rgb(49, 49, 49) 85%)`,
                borderImageSlice: 1,
            }}>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-12 p-4 md:p-8 lg:p-20 pt-8 md:pt-12 lg:pt-16 px-4 md:px-8 lg:px-36 bg-blackDark'>
                    <motion.div
                        className='text-3xl md:text-4xl leading-snug font-semibold mb-8 md:mb-0'
                        initial={{ x: -50, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{
                            type: "spring",
                            stiffness: 50,
                            damping: 20,
                            delay: 0.1
                        }}
                        viewport={{ once: true, amount: 0.5 }}
                    >
                        Reasons to select <br /> Enterprise <br /> App <br /> development
                    </motion.div>
                    {enterpriseData.ReliabilityEnterpriseData.map((reliable: any, index: number) => {
                         let img = getImage(reliable.image ?? "") ?? "";
                        return (
                            <motion.div
                                key={index}
                                className='group bg-darkGreyPrimary rounded-3xl flex flex-col items-center justify-between'
                                custom={index}
                                variants={itemVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.2 }}
                            >
                                <div
                                    className='rounded-[2rem] w-fit px-0 h-fit'
                                    style={{
                                        transition: 'all 0.1s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.02)';
                                        e.currentTarget.style.borderBottomWidth = '4px';
                                        e.currentTarget.style.borderBottomStyle = 'solid';
                                        e.currentTarget.style.borderBottomColor = `${reliable.bgColor}`;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                        e.currentTarget.style.borderBottomWidth = '0px';
                                    }}
                                >
                                    <motion.div className='bg-darkGreyPrimary rounded-3xl flex flex-col items-center justify-center p-8 h-60 relative overflow-hidden m-0 w-full'>
                                        <motion.div
                                            className={`w-[60px] h-[60px] p-4 rounded-3xl flex items-center justify-center`}
                                            initial={{ scale: 0.8, opacity: 0 }}
                                            whileInView={{ scale: 1, opacity: 1 }}
                                            transition={{ delay: index * 0.1 + 0.2 }}
                                            viewport={{ once: true }}
                                            whileHover={{ scale: 1.1 }}
                                        >
                                            <DynamicSVG color='#fff' size='w-full'>{img}</DynamicSVG>
                                        </motion.div>
                                        <motion.div
                                            className={`text-xl text-center font-semibold mb-1 text-${reliable.color}`}
                                            initial={{ opacity: 0 }}
                                            whileInView={{ opacity: 1 }}
                                            transition={{ delay: index * 0.1 + 0.3 }}
                                            viewport={{ once: true }}
                                        >
                                            {reliable.title}
                                        </motion.div>
                                        <motion.div
                                            className='text-lightGrey text-center text-sm px-4'
                                            initial={{ opacity: 0 }}
                                            whileInView={{ opacity: 1 }}
                                            transition={{ delay: index * 0.1 + 0.4 }}
                                            viewport={{ once: true }}
                                        >
                                            {reliable.subtitle}
                                        </motion.div>
                                        {/* <div className={`group-hover:border-b-4 group-hover:${(index === 0 || index === 4) ? 'border-yellow' : (index === 1 || index === 3) ? 'border-second' : 'border-purple'} pb-4 w-full mt-auto rounded-3xl  absolute bottom-0 hover:z-[1000]`}></div> */}
                                    </motion.div>
                                </div>
                            </motion.div>
                        )
                    })}
                </div>
            </div>
            <motion.div
                className='w-full'
                style={{
                    borderBottom: "1.5px solid transparent",
                    borderImageSource:
                        `linear-gradient(90deg, rgb(49, 49, 49) 51%,var(--color-primary) 81%, rgb(49, 49, 49) 93%, rgb(49, 49, 49) 85%)`,
                    borderImageSlice: 1,
                }}
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                transition={{ duration: 1, ease: "easeInOut" }}
                viewport={{ once: true, amount: 0.8 }}
            ></motion.div>
        </motion.div>
    )
}

export default ReliabilityEnterpriseSteps
